import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'react-toastify';
import type { InstagramChannel, InstagramLoginMethod, MetaDmAccessMode } from '@workspace/types';
import { EvoHubConnectPanel } from '@/features/evohub/components/EvoHubConnectPanel';
import { instagramAdminApi } from '../api/instagramApi';
import { InstagramUserWhitelistSection } from './InstagramUserWhitelistSection';

type InstagramChannelPanelProps = {
  instanceBotId: string;
  botName: string;
  onChannelChange?: () => void;
};

type ChannelFormState = {
  loginMethod: InstagramLoginMethod;
  accessMode: MetaDmAccessMode;
  whitelistRejectionEnabled: boolean;
  whitelistRejectionMessage: string;
};

const emptyForm: ChannelFormState = {
  loginMethod: 'INSTAGRAM_API',
  accessMode: 'OPEN',
  whitelistRejectionEnabled: false,
  whitelistRejectionMessage: '',
};

export function InstagramChannelPanel({
  instanceBotId,
  botName,
  onChannelChange,
}: InstagramChannelPanelProps) {
  const [channel, setChannel] = useState<InstagramChannel | null>(null);
  const [form, setForm] = useState<ChannelFormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadChannel = useCallback(async () => {
    try {
      setLoading(true);
      const existing = await instagramAdminApi.getChannelByInstanceBot(instanceBotId);
      setChannel(existing);
      if (existing) {
        setForm({
          loginMethod: existing.loginMethod,
          accessMode: existing.accessMode,
          whitelistRejectionEnabled: existing.whitelistRejectionEnabled,
          whitelistRejectionMessage: existing.whitelistRejectionMessage ?? '',
        });
      } else {
        setForm(emptyForm);
      }
    } catch (error) {
      toast.error((error as Error).message || 'Error al cargar Instagram');
    } finally {
      setLoading(false);
    }
  }, [instanceBotId]);

  useEffect(() => {
    void loadChannel();
  }, [loadChannel]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const trimmedRejection = form.whitelistRejectionMessage.trim();
      if (channel) {
        const updated = await instagramAdminApi.updateChannel(channel.id, {
          accessMode: form.accessMode,
          whitelistRejectionEnabled: form.whitelistRejectionEnabled,
          whitelistRejectionMessage: trimmedRejection || null,
        });
        setChannel(updated);
        toast.success('Canal Instagram actualizado');
        onChannelChange?.();
      } else {
        const created = await instagramAdminApi.createChannel({
          instanceBotId,
          name: `Instagram ${botName}`.slice(0, 120),
          loginMethod: form.loginMethod,
          accessMode: form.accessMode,
          whitelistRejectionEnabled: form.whitelistRejectionEnabled,
          whitelistRejectionMessage: trimmedRejection || undefined,
        });
        setChannel(created);
        toast.success('Canal Instagram creado. Abrí el enlace Evo Hub para conectar la cuenta.');
        onChannelChange?.();
      }
    } catch (error) {
      toast.error((error as Error).message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground py-4'>Cargando canal Instagram…</p>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex flex-wrap items-center gap-2'>
          Canal Instagram DM del bot &quot;{botName}&quot;
          {channel?.instagramUsername ? (
            <Badge variant='secondary'>@{channel.instagramUsername}</Badge>
          ) : null}
        </CardTitle>
        <CardDescription>
          Webhook Evo Hub:{' '}
          <code className='text-xs break-all'>
            {channel?.webhookUrl ?? 'se genera al crear el canal'}
          </code>
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        {!channel ? (
          <div>
            <Label>Método de login</Label>
            <select
              className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              value={form.loginMethod}
              onChange={e =>
                setForm({ ...form, loginMethod: e.target.value as InstagramLoginMethod })
              }
            >
              <option value='INSTAGRAM_API'>Instagram Business Login (recomendado)</option>
              <option value='FACEBOOK'>Vía Facebook Page</option>
            </select>
            <p className='text-xs text-muted-foreground mt-1'>
              El método de login no se puede cambiar después de crear el canal.
            </p>
          </div>
        ) : (
          <div className='text-sm text-muted-foreground'>
            Login: {channel.loginMethod === 'FACEBOOK' ? 'Vía Facebook' : 'Instagram API directo'}
          </div>
        )}

        <div>
          <Label>Modo de acceso</Label>
          <select
            className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            value={form.accessMode}
            onChange={e => setForm({ ...form, accessMode: e.target.value as MetaDmAccessMode })}
          >
            <option value='OPEN'>Abierto</option>
            <option value='WHITELIST_USERS'>Whitelist de usuarios</option>
          </select>
        </div>

        <div className='flex flex-wrap gap-2'>
          <Button type='button' onClick={() => void handleSave()} disabled={saving}>
            {channel ? 'Guardar cambios' : 'Crear canal'}
          </Button>
        </div>

        {channel ? (
          <>
            <EvoHubConnectPanel
              connectUrl={channel.evoHubConnectUrl}
              status={channel.status}
              linkedAt={channel.hubLinkedAt}
              lastError={channel.hubLastError}
            />
            <Tabs defaultValue='whitelist'>
              <TabsList>
                <TabsTrigger value='whitelist'>Whitelist</TabsTrigger>
              </TabsList>
              <TabsContent value='whitelist' className='mt-4'>
                <InstagramUserWhitelistSection
                  channelId={channel.id}
                  accessMode={form.accessMode}
                  whitelistRejectionEnabled={form.whitelistRejectionEnabled}
                  whitelistRejectionMessage={form.whitelistRejectionMessage}
                  onRejectionChange={(enabled, message) =>
                    setForm({
                      ...form,
                      whitelistRejectionEnabled: enabled,
                      whitelistRejectionMessage: message,
                    })
                  }
                  showRejectionSettings
                  showSaveReminder
                  compact
                />
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <p className='text-sm text-muted-foreground'>
            Al crear el canal se registrará en Evo Hub. Luego abrí el enlace para conectar la cuenta
            de Instagram Business.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
